import { call, getContext, put, select, takeEvery } from 'redux-saga/effects';
import { takeEveryPromised } from 'next/lib/effects';
import { patterns as flowPatterns } from 'next/entities/flows';
import { patterns as pinPatterns } from 'next/entities/pins';
import { selectUser } from 'next/entities/user';
import { flush, insert, patterns, prune, reject, resolve } from './actions';
import { parse } from './shape';

/**
 * Normalize tags summary response as collection
 *
 * @param {Response} response - Raw tags summary response
 * @return {Collection} Tags collection
 */
const transform = response =>
  Object.entries(response).reduce((acc, [id, tag]) => {
    acc[id] = { ...parse(tag), id };
    return acc;
  }, {});

function* fetchTags() {
  try {
    const api = yield getContext('api');
    const response = yield call(api.getTags);
    yield put(resolve(transform(response || {})));
  } catch (error) {
    yield put(reject(error));
  }
}

function* createTag({ payload: data }) {
  try {
    /**
     * NOTE: Currently, the client side creates the entire tag model which may
     *       include fields that are not used anywhere else...
     */
    const user = yield select(selectUser);
    const tag = {
      ...data,
      createdAt: Date.now(),
      createdBy: user.id,
      // TODO: Add `index`?
    };

    const api = yield getContext('api');
    const { name: id } = yield call(api.createTag, tag);

    /**
     * NOTE: Additionally when creating the record in Firebase using the REST
     *       API, the `id` attribute is not set on the record itself so a
     *       subsequent update request is made to set...
     */
    const complete = { id, ...tag };
    yield call(api.updateTag, id, complete);

    yield put(insert(complete));

    return complete;
  } catch (error) {
    yield put(reject(error));
    throw error;
  }
}

function* updateTag({ payload: { id, delta } }) {
  try {
    const api = yield getContext('api');
    yield call(api.updateTag, id, delta);
    yield put(flush(id));
  } catch (error) {
    yield put(reject(error));
    throw error;
  }
}

function* removeTag({ payload: { id } }) {
  try {
    const api = yield getContext('api');
    yield call(api.deleteTag, id);
    yield put(prune(id));
  } catch (error) {
    yield put(reject(error));
    throw error;
  }
}

export default function* saga() {
  // Internal actions
  yield takeEvery(patterns.read, fetchTags);
  yield takeEveryPromised(patterns.create, createTag);
  yield takeEveryPromised(patterns.update, updateTag);
  yield takeEveryPromised(patterns.remove, removeTag);

  // External actions
  yield takeEvery(flowPatterns.read, fetchTags);
  yield takeEvery(pinPatterns.read, fetchTags);
}
